import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { UserPlus } from 'lucide-react'
import { usersApi } from '@/api/users'
import { toast } from '@/store/toast'
import Spinner from '@/components/common/Spinner'

const schema = z.object({
  email: z.string().email('Корректный email'),
})
type FormData = z.infer<typeof schema>

/**
 * Страница ГИПа «Сотрудники»: приглашение проектировщиков и список проектировщиков.
 * ГИП видит только имена и приглашает только проектировщиков; блокировка, удаление
 * и приглашение других ролей остаются за директором и заместителем.
 */
export default function GipStaffPage() {
  const qc = useQueryClient()
  const [serverError, setServerError] = useState<string | null>(null)

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users-assignable'],
    queryFn: () => usersApi.assignable().then((r) => r.data),
  })
  const designers = users.filter((u) => u.role === 'designer' && u.is_active)

  const {
    register, handleSubmit, reset, formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const inviteMutation = useMutation({
    mutationFn: (email: string) => usersApi.create({ email, role: 'designer' }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['users-assignable'] })
      toast.success(`Приглашение отправлено на ${res.data.email}`)
      setServerError(null)
      reset()
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setServerError('Сотрудник с таким адресом уже есть в системе. Если его нет в списке, обратитесь к директору.')
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        setServerError('Слишком много приглашений за час. Повторите позже.')
      } else {
        setServerError('Не удалось отправить приглашение. Повторите позже.')
      }
    },
  })

  if (isLoading) return <Spinner size="lg" />

  const inputCls =
    'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <h1 className="text-xl font-semibold text-gray-900">Сотрудники</h1>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
        <h2 className="font-medium text-gray-900 flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Пригласить проектировщика
        </h2>
        <p className="text-sm text-gray-500">
          Введите рабочую почту сотрудника. Ему придёт письмо со ссылкой: по ней он задаёт пароль и
          заходит в систему. После этого его можно назначить исполнителем раздела.
          Блокировка и удаление сотрудников, а также приглашение ГИПов и заместителей остаются за директором.
        </p>
        <form
          noValidate
          onSubmit={handleSubmit((d) => inviteMutation.mutate(d.email.trim()))}
          className="flex flex-col sm:flex-row gap-2"
        >
          <div className="flex-1">
            <input
              type="email"
              placeholder="email@организация.ru"
              {...register('email')}
              className={inputCls}
            />
            {errors.email && <p className="mt-1 text-xs text-rose-600">{errors.email.message}</p>}
          </div>
          <button
            type="submit"
            disabled={inviteMutation.isPending}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {inviteMutation.isPending ? 'Отправка…' : 'Отправить приглашение'}
          </button>
        </form>
        {serverError && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{serverError}</p>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
        <h2 className="font-medium text-gray-900">Проектировщики ({designers.length})</h2>
        {designers.length === 0 ? (
          <p className="text-sm text-gray-500">Пока никого нет. Пригласите первого сотрудника выше.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {designers.map((u) => (
              <li key={u.id} className="py-2 text-sm text-gray-800">
                {u.full_name ?? 'Не заполнил профиль (приглашение отправлено)'}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
